import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Play } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter186 — Onboarding Skip Resume banner.
 *
 * Renders a soft one-line prompt on the dashboard for any user whose
 * onboarding is in `skipped=true` state. Tapping "Resume" clears the
 * skip flag (POST /api/onboarding/resume) and navigates to /onboarding
 * where the user picks up from where they left off. Tapping "×" hides
 * the banner for THIS session only — the state stays `skipped=true` so
 * the 3-day scheduler can still fire a follow-up.
 */
export default function OnboardingResumeBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  // Iter186 — dismiss survives page reloads within the same tab via
  // sessionStorage, resets on new login (sessionStorage clears on tab close).
  const [dismissedThisSession, setDismissedThisSession] = useState(() => {
    try {
      return typeof window !== "undefined" && window.sessionStorage.getItem("warmap_onboarding_resume_dismissed") === "1";
    } catch { return false; }
  });

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/onboarding/status`, { credentials: "include" });
      if (!r.ok) return;
      const data = await r.json();
      // Only show for users who explicitly skipped AND haven't completed.
      if (data?.skipped && !data?.completed) setVisible(true);
    } catch {
      /* silent — banner is best-effort */
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (!visible || dismissedThisSession) return null;

  const handleResume = async () => {
    try {
      await fetch(`${API}/onboarding/resume`, {
        method: "POST",
        credentials: "include",
      });
    } catch { /* fail-open — the /onboarding page will load anyway */ }
    // Clear the session dismiss on resume — user actively engaged.
    try { window.sessionStorage.removeItem("warmap_onboarding_resume_dismissed"); } catch { /* private-mode */ }
    navigate("/onboarding");
  };

  const handleDismiss = () => {
    setDismissedThisSession(true);
    try { window.sessionStorage.setItem("warmap_onboarding_resume_dismissed", "1"); } catch { /* private-mode */ }
  };

  return (
    <div
      data-testid="onboarding-resume-banner"
      className="flex items-center gap-3 px-4 py-3 bg-emerald-500/8 border border-emerald-500/25 rounded-xl"
    >
      <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
        <Sparkles className="w-4 h-4 text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white">You skipped the intro — want to pick up where you left off?</div>
        <div className="text-xs text-gray-400">Two minutes. Unlocks your leaderboard rank, EOD form, and daily rhythm.</div>
      </div>
      <Button
        data-testid="onboarding-resume-btn"
        size="sm"
        onClick={handleResume}
        className="bg-emerald-500 hover:bg-emerald-600 text-white"
      >
        <Play className="w-3.5 h-3.5 mr-1.5" /> Resume
      </Button>
      <button
        type="button"
        aria-label="Dismiss for this session"
        onClick={handleDismiss}
        className="text-gray-500 hover:text-white transition-colors p-1"
        data-testid="onboarding-resume-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
